import React, { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { ReactComponentLike } from 'prop-types';
import pluralize from 'pluralize';

import AdminPageTabs from './adminPageTabs.component';
import ViewGrid from './viewGrid.component';
import { TabTypes } from './constants';
import HamburgerDropdown from './hamburgerDropdown.component';

import PeriodDropdown from '../../shared/periodDropdown.component';
import { ReduxAlert } from '../../shared/alert.component';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';

import SecondaryMenu from '../../menu/secondaryMenu.component';

import { modalDataSelector } from '../../shared/modals/modals.selectors';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';

import {
  periodOrCurrentSelector,
  currentPeriodSelector,
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import getSelectors from './store/selectorsFactory';
import { Actions, ComparatorConfig, Period, VoidParametrisedFunction } from './types';
import { ColumnDefinitions } from '../../shared/types/common';
import { ReduxState } from '../../shared/reduxStateType';

type AdminImportPageDispatchProps = {
  showModalForAddEdit: VoidParametrisedFunction,
  hideModalForAddEdit: VoidParametrisedFunction,
  showModalForRemove: VoidParametrisedFunction,
  hideModalForRemove: VoidParametrisedFunction,
  fetchEntities?: VoidParametrisedFunction,
  saveEntity?: VoidParametrisedFunction,
  removeEntity?: VoidParametrisedFunction,
  uploadEntitiesFromFile?: VoidParametrisedFunction,
  importEntities?: VoidParametrisedFunction,
  clearUploadedEntities?: VoidParametrisedFunction,
}

type AdminImportPageStateProps = {
  addEditModalData: {
    show: boolean,
    data?: Record<string, any>,
  },
  removeModalData: {
    show: boolean,
    data?: {
      entity: Record<string, any>,
      rowIndex: number,
    },
  },

  period: Period,
  currentPeriod: Period,

  environmentSuffix: string,
  shouldDisplayZero: boolean,
  hideDecimals: boolean,
  negativeMinus: boolean,

  additionalData: any, // eslint-disable-line react/forbid-prop-types
  isFetchingEntities: boolean,
  uploadingEntities: boolean,
  importingEntities: boolean,
  isRemovingInProgress: boolean,
  entities: Record<string, any>[],
  uploadedEntities: Record<string, any>[],
  areImportsPending: boolean,
}

type AdminImportPageOwnProps = {
  pageTitle: string,
  pageName: string,
  canModify?: boolean,
  AddEditEntityModal?: ReactComponentLike,
  RemoveEntityModal?: ReactComponentLike,
  addEntityMessage?: string,
  importColumnDefs?: ColumnDefinitions,
  columnDefs: ColumnDefinitions,
  generateColumnDefs?: Function,
  actions: Actions,
  exportToExcel?: Function,
  generateTemplateFileName?: Function,
  templatePath?: string,
  generateTemplate?: VoidParametrisedFunction,
  disableGeneratingTemplate?: boolean,
  disableImporting?: boolean,
  disableExporting?: boolean,
  disableAddingEntity?: boolean,
  comparatorConfig: ComparatorConfig,
  adminColumnRenderer?: ReactComponentLike,
  additionalHamburgerMenuItems?: Node[],
  hasPeriodControl?: boolean,
  singularTitle?: boolean,
  fetchingEntitiesSelector?: Function,
  uploadingEntitiesSelector?: Function,
  importingEntitiesSelector?: Function,
  entitiesSelector?: Function,
  uploadedEntitiesSelector?: Function,
  areImportsPendingSelector?: Function,
  removingEntitySelector?: Function,
  additionalDataSelector?: Function,
  initialFilters?: Record<string, any>,
  hideImporting?: boolean,
  hideAdd?: boolean,
}

type AdminImportPageProps = AdminImportPageStateProps & AdminImportPageDispatchProps & AdminImportPageOwnProps;

const AdminImportPage = ({
  pageTitle,
  canModify = false,
  AddEditEntityModal,
  RemoveEntityModal,
  addEntityMessage,
  importColumnDefs: importBaseColumnDefs,
  columnDefs: baseColumnDefs,
  generateColumnDefs,
  exportToExcel,
  generateTemplateFileName,
  templatePath,
  generateTemplate,
  disableGeneratingTemplate,
  disableImporting,
  disableExporting,
  comparatorConfig,
  adminColumnRenderer,
  additionalHamburgerMenuItems,
  hasPeriodControl = true,
  singularTitle = false,
  disableAddingEntity = false,

  addEditModalData,
  removeModalData,

  period,
  currentPeriod,

  isFetchingEntities,
  uploadingEntities,
  importingEntities,
  isRemovingInProgress,
  entities,
  uploadedEntities,
  areImportsPending,

  environmentSuffix,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,

  showModalForAddEdit,
  hideModalForAddEdit,
  hideModalForRemove,
  fetchEntities,
  saveEntity,
  removeEntity,
  uploadEntitiesFromFile,
  importEntities,
  clearUploadedEntities,

  additionalData,

  initialFilters,
  hideImporting,
  hideAdd,
}: AdminImportPageProps) => {
  const [activeTab, changeTab] = useState(TabTypes.VIEW);

  useEffect(() => {
    if (fetchEntities && !importingEntities) {
      fetchEntities({ periodName: period.name });
      changeTab(TabTypes.VIEW);
    }
    return () => clearUploadedEntities?.();
  }, [fetchEntities, period, importingEntities, clearUploadedEntities]);

  const fileToImportChange = useCallback(event => {
    uploadEntitiesFromFile?.({ file: event.target.files[0], currentPeriod });
  }, [uploadEntitiesFromFile, currentPeriod]);

  const fileToImportSelect = useCallback(hideDropDown => () => {
    changeTab(TabTypes.IMPORT);
    hideDropDown();
  }, []);

  const templateFileName = generateTemplateFileName
    ? `${generateTemplateFileName({ periodName: period.name })}${environmentSuffix}.xlsx`
    : null;

  const selectFileToUpload = useCallback(file => {
    uploadEntitiesFromFile?.({ file, currentPeriod });
  }, [uploadEntitiesFromFile, currentPeriod]);

  const columnDefs = generateColumnDefs
    ? generateColumnDefs(baseColumnDefs, additionalData)
    : baseColumnDefs;

  const importColumnDefs = generateColumnDefs
    ? generateColumnDefs(importBaseColumnDefs || columnDefs, additionalData)
    : (importBaseColumnDefs || columnDefs);

  const exportToExcelHandler = useCallback(callback => {
    exportToExcel?.({
      entities,
      periodName: period.name,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      columnDefs,
      additionalData,
    });
    callback();
  }, [
    entities, period, environmentSuffix, shouldDisplayZero,
    hideDecimals, negativeMinus, columnDefs, exportToExcel, additionalData,
  ]);

  const title = singularTitle ? pageTitle : pluralize.plural(pageTitle);

  return (
    <div className="admin-import-page">
      <NavigationPrompt
        show={areImportsPending}
        message={<span>{IMPORTED_DATA_PENDING_MESSAGE}</span>}
      />
      <SecondaryMenu>
        {hasPeriodControl ? <PeriodDropdown /> : null}
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModify}
            showModalForAdd={showModalForAddEdit}
            addEntityMessage={addEntityMessage}
            fileToImportChange={uploadEntitiesFromFile && fileToImportChange}
            fileToImportSelect={uploadEntitiesFromFile && fileToImportSelect}
            templateFileName={templateFileName}
            templatePath={templatePath}
            generateTemplate={generateTemplate}
            disableGeneratingTemplate={disableGeneratingTemplate}
            disableExporting={disableExporting}
            disableImporting={disableImporting}
            exportToExcel={exportToExcel && exportToExcelHandler}
            additionalItems={additionalHamburgerMenuItems}
            disableAddingEntity={disableAddingEntity}
            hideImporting={hideImporting}
            hideAdd={hideAdd}
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <ReduxAlert/>
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>{title}</h5>
              </div>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="col">
              {uploadEntitiesFromFile && !hideImporting
                ? (
                  <AdminPageTabs
                    canModify={canModify}
                    pageTitle={pageTitle}
                    activeTab={activeTab}
                    changeTab={changeTab}
                    importColumnDefs={importColumnDefs}
                    columnDefs={columnDefs}
                    isFetchingEntities={isFetchingEntities}
                    entities={entities}
                    selectFileToUpload={selectFileToUpload}
                    uploadedEntities={uploadedEntities}
                    uploadingEntities={uploadingEntities}
                    importEntities={importEntities}
                    comparatorConfig={comparatorConfig}
                    adminColumnRenderer={adminColumnRenderer}
                    templateFileName={templateFileName}
                    templatePath={templatePath}
                    generateTemplate={generateTemplate}
                    disableGeneratingTemplate={disableGeneratingTemplate}
                    disableImporting={disableImporting}
                    additionalData={additionalData}
                    initialFilters={initialFilters}
                  />
                )
                : <ViewGrid
                  adminColumnRenderer={adminColumnRenderer}
                  className="admin-import-grid"
                  columnDefs={columnDefs}
                  data={entities}
                  isLoading={isFetchingEntities}
                  shouldDisplayAdminColumn={canModify}
                  initialFilters={initialFilters}
                />
              }
            </div>
          </div>
        </div>
      </div>
      {AddEditEntityModal && canModify && <AddEditEntityModal
        entityToEdit={addEditModalData && addEditModalData.data}
        hideModal={hideModalForAddEdit}
        saveEntity={saveEntity}
        period={period}
        isVisible={addEditModalData && addEditModalData.show}
        openForEdit={Boolean(addEditModalData && addEditModalData.data)}
      />}
      {RemoveEntityModal && canModify && <RemoveEntityModal
        entityToRemove={removeModalData && removeModalData.data && removeModalData.data.entity}
        hideModal={hideModalForRemove}
        removeEntity={removeEntity}
        isRemovingInProgress={isRemovingInProgress}
        period={period}
        rowIndex={removeModalData && removeModalData.data && removeModalData.data.rowIndex}
        isVisible={removeModalData && removeModalData.show}
      />}
    </div>
  );
};

const mapStateToProps = (state: ReduxState, props: AdminImportPageOwnProps) => {
  const generatedSelectors = getSelectors(props.pageName);

  const {
    fetchingEntitiesSelector = generatedSelectors.fetchingEntitiesSelector,
    uploadingEntitiesSelector = generatedSelectors.uploadingEntitiesSelector,
    importingEntitiesSelector = generatedSelectors.importingEntitiesSelector,
    entitiesSelector = generatedSelectors.entitiesSelector,
    uploadedEntitiesSelector = generatedSelectors.uploadedEntitiesSelector,
    areImportsPendingSelector = generatedSelectors.areImportsPendingSelector,
    removingEntitySelector = generatedSelectors.removingEntitySelector,
    additionalDataSelector = () => null,
  } = props;

  return {
    addEditModalData: props.AddEditEntityModal ? modalDataSelector(state, `${props.pageName}/addEdit`) : null,
    removeModalData: props.RemoveEntityModal ? modalDataSelector(state, `${props.pageName}/remove`) : null,

    period: periodOrCurrentSelector(state),
    currentPeriod: currentPeriodSelector(state),

    environmentSuffix: environmentSuffixSelector(state),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),

    isFetchingEntities: fetchingEntitiesSelector(state),
    uploadingEntities: uploadingEntitiesSelector(state),
    importingEntities: importingEntitiesSelector(state),
    isRemovingInProgress: removingEntitySelector(state),
    entities: entitiesSelector(state),
    uploadedEntities: uploadedEntitiesSelector(state),
    areImportsPending: areImportsPendingSelector(state),
    additionalData: additionalDataSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: { actions: Actions }) =>
  bindActionCreators(props.actions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AdminImportPage);
